/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { TokenTransferStatusEnum } from './TokenTransferStatusEnum';
import {
    TokenTransferStatusEnumFromJSON,
    TokenTransferStatusEnumFromJSONTyped,
    TokenTransferStatusEnumToJSON,
} from './TokenTransferStatusEnum';
import type { EthTokenTransferInfo } from './EthTokenTransferInfo';
import {
    EthTokenTransferInfoFromJSON,
    EthTokenTransferInfoFromJSONTyped,
    EthTokenTransferInfoToJSON,
} from './EthTokenTransferInfo';
import type { TokenContractInfo } from './TokenContractInfo';
import {
    TokenContractInfoFromJSON,
    TokenContractInfoFromJSONTyped,
    TokenContractInfoToJSON,
} from './TokenContractInfo';
import type { TokenTransferStatusUpdate } from './TokenTransferStatusUpdate';
import {
    TokenTransferStatusUpdateFromJSON,
    TokenTransferStatusUpdateFromJSONTyped,
    TokenTransferStatusUpdateToJSON,
} from './TokenTransferStatusUpdate';
import type { StellarTokenTransferInfo } from './StellarTokenTransferInfo';
import {
    StellarTokenTransferInfoFromJSON,
    StellarTokenTransferInfoFromJSONTyped,
    StellarTokenTransferInfoToJSON,
} from './StellarTokenTransferInfo';

/**
 * 
 * @export
 * @interface TokenTransfer
 */
export interface TokenTransfer {
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly amount: string;
    /**
     * 
     * @type {Date}
     * @memberof TokenTransfer
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {EthTokenTransferInfo}
     * @memberof TokenTransfer
     */
    ethInfo: EthTokenTransferInfo;
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly fromNetworkAddress: string;
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly fromWalletId: string | null;
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly id: string;
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly productName: string;
    /**
     * Recipient as shown in the transactions table: '{{BURN}}', '{{WALLET_<WalletType>}}' for a wallet of the investor, or the network address
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly recipientColumn: string;
    /**
     * Sender as shown in the transactions table: '{{ISSUANCE}}', '{{WALLET_<WalletType>}}' for a wallet of the investor, or the network address
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly senderColumn: string;
    /**
     * 
     * @type {Array<TokenTransferStatusUpdate>}
     * @memberof TokenTransfer
     */
    statusUpdates: Array<TokenTransferStatusUpdate>;
    /**
     * 
     * @type {TokenTransferStatusEnum}
     * @memberof TokenTransfer
     */
    readonly status: TokenTransferStatusEnum;
    /**
     * 
     * @type {StellarTokenTransferInfo}
     * @memberof TokenTransfer
     */
    stellarInfo: StellarTokenTransferInfo;
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly toNetworkAddress: string;
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly toWalletId: string | null;
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly tokenId: string;
    /**
     * 
     * @type {TokenContractInfo}
     * @memberof TokenTransfer
     */
    readonly tokenizationInfo: TokenContractInfo;
    /**
     * 
     * @type {string}
     * @memberof TokenTransfer
     */
    readonly twoFactorRequestId: string | null;
}



/**
 * Check if a given object implements the TokenTransfer interface.
 */
export function instanceOfTokenTransfer(value: object): value is TokenTransfer {
    if (!('amount' in value) || value['amount'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('ethInfo' in value) || value['ethInfo'] === undefined) return false;
    if (!('fromNetworkAddress' in value) || value['fromNetworkAddress'] === undefined) return false;
    if (!('fromWalletId' in value) || value['fromWalletId'] === undefined) return false;
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('productName' in value) || value['productName'] === undefined) return false;
    if (!('recipientColumn' in value) || value['recipientColumn'] === undefined) return false;
    if (!('senderColumn' in value) || value['senderColumn'] === undefined) return false;
    if (!('statusUpdates' in value) || value['statusUpdates'] === undefined) return false;
    if (!('status' in value) || value['status'] === undefined) return false;
    if (!('stellarInfo' in value) || value['stellarInfo'] === undefined) return false;
    if (!('toNetworkAddress' in value) || value['toNetworkAddress'] === undefined) return false;
    if (!('toWalletId' in value) || value['toWalletId'] === undefined) return false;
    if (!('tokenId' in value) || value['tokenId'] === undefined) return false;
    if (!('tokenizationInfo' in value) || value['tokenizationInfo'] === undefined) return false;
    if (!('twoFactorRequestId' in value) || value['twoFactorRequestId'] === undefined) return false;
    return true;
}

export function TokenTransferFromJSON(json: any): TokenTransfer {
    return TokenTransferFromJSONTyped(json, false);
}

export function TokenTransferFromJSONTyped(json: any, ignoreDiscriminator: boolean): TokenTransfer {
    if (json == null) {
        return json;
    }
    return {
        
        'amount': json['amount'],
        'createdAt': (new Date(json['created_at'])),
        'ethInfo': EthTokenTransferInfoFromJSON(json['eth_info']),
        'fromNetworkAddress': json['from_network_address'],
        'fromWalletId': json['from_wallet_id'],
        'id': json['id'],
        'productName': json['product_name'],
        'recipientColumn': json['recipient_column'],
        'senderColumn': json['sender_column'],
        'statusUpdates': ((json['status_updates'] as Array<any>).map(TokenTransferStatusUpdateFromJSON)),
        'status': TokenTransferStatusEnumFromJSON(json['status']),
        'stellarInfo': StellarTokenTransferInfoFromJSON(json['stellar_info']),
        'toNetworkAddress': json['to_network_address'],
        'toWalletId': json['to_wallet_id'],
        'tokenId': json['token_id'],
        'tokenizationInfo': TokenContractInfoFromJSON(json['tokenization_info']),
        'twoFactorRequestId': json['two_factor_request_id'],
    };
}

export function TokenTransferToJSON(value?: Omit<TokenTransfer, 'amount'|'created_at'|'from_network_address'|'from_wallet_id'|'id'|'product_name'|'recipient_column'|'sender_column'|'status'|'to_network_address'|'to_wallet_id'|'token_id'|'tokenization_info'|'two_factor_request_id'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'eth_info': EthTokenTransferInfoToJSON(value['ethInfo']),
        'status_updates': ((value['statusUpdates'] as Array<any>).map(TokenTransferStatusUpdateToJSON)),
        'stellar_info': StellarTokenTransferInfoToJSON(value['stellarInfo']),
    };
}

